import { Button, type Key, Tabs, Typography } from "@heroui/react";
import { Icon } from "@iconify/react";
import {
  Navigate,
  Outlet,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";

import { CHANNEL_SIDEBAR_CONFIG } from "./sidebar-config";

import LoadingScreen from "@/components/loading";
import { useGetChannel } from "@/features/channel/api";
import { ChannelIcon } from "@/features/channel/components/channel-icon";
import { getChannelPlatformName } from "@/utils/channel";

export const ManageChannels = () => {
  const { id } = useParams<{ id: string }>();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { data: channel, isLoading } = useGetChannel(id!);

  if (isLoading) {
    return <LoadingScreen />;
  }

  if (!channel) {
    return <Navigate replace to={"/channels"} />;
  }

  const tabs = CHANNEL_SIDEBAR_CONFIG[channel.type];
  const platformName = getChannelPlatformName(channel.type);

  const segments = pathname.split("/").filter(Boolean);
  const currentSegment = segments[segments.indexOf(id!) + 1] ?? "";
  const selectedKey =
    (tabs.find((tab) => tab.href === currentSegment) ?? tabs[0])?.key ?? "";

  const handleSelectionChange = (key: Key) => {
    const tab = tabs.find((item) => item.key === key);

    if (!tab) return;

    navigate(`/channels/manage/${id}${tab.href ? `/${tab.href}` : ""}`);
  };

  return (
    <div className="flex h-full w-full flex-col overflow-hidden">
      <div className="flex items-center gap-2 px-3 pt-2">
        <Button
          isIconOnly
          aria-label="Back to channels"
          size="sm"
          variant="tertiary"
          onPress={() => navigate("/channels")}
        >
          <Icon fontSize={18} icon="solar:alt-arrow-left-linear" />
        </Button>
        <ChannelIcon size={18} type={channel.type} />
        <Typography className="leading-tight" type="body-sm" weight="semibold">
          {channel.name}
        </Typography>
        <Typography className="leading-tight" color="muted" type="body-xs">
          {platformName}
        </Typography>
      </div>
      <div className="border-b border-border px-4">
        <Tabs
          selectedKey={selectedKey}
          variant="secondary"
          onSelectionChange={handleSelectionChange}
        >
          <Tabs.ListContainer>
            <Tabs.List
              aria-label="Channel settings"
              className="gap-6 border-b-0"
            >
              {tabs.map((tab) => (
                <Tabs.Tab
                  key={tab.key}
                  className="h-10 w-fit flex-none gap-2 px-0 text-[13px] font-medium text-muted data-[selected=true]:text-foreground"
                  id={tab.key}
                >
                  {tab.icon && <Icon fontSize={16} icon={tab.icon} />}
                  {tab.title}
                  <Tabs.Indicator className="h-0.5 rounded-none bg-accent" />
                </Tabs.Tab>
              ))}
            </Tabs.List>
          </Tabs.ListContainer>
        </Tabs>
      </div>
      <div className="min-h-0 flex-1 overflow-hidden">
        <Outlet />
      </div>
    </div>
  );
};
